import React from 'react';
import { ArrowRight } from 'lucide-react';
import SlimGridMotion from '../components/SlimGridMotion';
import OptionsAccordion from '../components/services/OptionsAccordion';
import UspStrip from '../components/services/UspStrip';
import Reveal from '../components/home/Reveal';
import GreenButton from '../components/home/GreenButton';
import SectionHeading from '../components/home/SectionHeading';
import HowItWorks from '../components/home/HowItWorks';
import { useContactModal } from '../contexts/ContactModalContext';
import type { ServiceOption } from '../data/services/types';

const gridImages = [
  '/home/display/oc-flag.jpg',
  '/home/gazebos/curry-king-gazebo.jpg',
  '/home/parasols/petal-bar-parasol.jpg',
  '/home/tablecloths/tablecloths-conference-studio.jpg',
  '/home/gazebos/wildjac-gazebo.jpg',
  '/home/parasols/wildjac-parasol.jpg',
  '/home/gazebos/barkavenue-gazebo.jpg',
  '/home/tablecloths/tablecloths-box-fitted-studio.jpg',
  '/home/gazebos/mayfield-gazebo.jpg',
  '/home/parasols/hodgehill-parasol.jpg',
  '/home/gazebos/nightstop-gazebo.jpg',
  '/home/services/tablecloths.jpg',
  '/home/gazebos/copper-beech.jpg',
  '/home/parasols/parasol-hero.jpg',
  '/home/gazebos/curry-king-gazebo-2.jpg',
  '/home/services/gazebos.jpg',
  '/home/gazebos/wildjac-gazebo-2.jpg',
  '/home/services/parasols.jpg',
  '/home/display/oc-flag.jpg',
  '/home/gazebos/heavy-frame.jpg',
  '/home/parasols/parasol-mockup.jpg',
];

const displayOptions: ServiceOption[] = [
  {
    id: 'gazebos-parasols',
    title: 'Gazebos & Parasols',
    lead: 'High-impact outdoor branding for events, forecourts and hospitality.',
    body: 'Heavy-duty pop-up gazebo frames with full-colour printed canopies, valances and walls, paired with commercial-grade printed parasols for pavement seating and beer gardens.',
    image: '/home/gazebos/gazebo-header.jpg',
    buttons: [
      { label: 'Gazebos', to: '/services/gazebos' },
      { label: 'Parasols', to: '/services/parasols' },
    ],
  },
  {
    id: 'banners',
    title: 'Banners',
    lead: 'Outdoor cafe barriers and heavy-duty PVC display banners.',
    body: 'Demarcate your outdoor space with printed cafe barrier systems and posts with weighted bases, or hang durable eyeleted PVC banners for festivals, markets and premises.',
    image: '/home/parasols/petal-bar-parasol.jpg',
    buttons: [
      { label: 'Cafe Barriers', to: '/services/cafe-barriers' },
      { label: 'PVC Banners', to: '/services/pvc-banners' },
    ],
  },
  {
    id: 'expos',
    title: 'Expos',
    lead: 'Fast-assembly exhibition displays and portable promotional stands.',
    body: 'Pop-up backdrops, tension-fabric counters and portable pull-up roller banners designed for quick setup and breakdown at trade shows, conferences and exhibitions.',
    image: '/home/gazebos/barkavenue-gazebo.jpg',
    buttons: [
      { label: 'Pop Up Displays', to: '/services/pop-up-displays' },
      { label: 'Pull Up Banners', to: '/services/pull-up-banners' },
    ],
  },
  {
    id: 'flags',
    title: 'Flags',
    lead: 'High-visibility outdoor feather and teardrop flags.',
    body: 'Printed feather and teardrop flags on ground spikes, cross bases or water-fill bases. Get noticed from the roadside, event entrances and forecourts.',
    image: '/home/display/oc-flag.jpg',
    buttons: [
      { label: 'Feather Flags', to: '/services/feather-flags' },
    ],
  },
  {
    id: 'carpet-mats',
    title: 'Carpet mats',
    lead: 'Branded entrance mats and exhibition flooring.',
    body: 'Heavy-duty printed logo mats and floor runners for showrooms, gyms, receptions and exhibition booths. Tough, non-slip and machine washable.',
    image: '/home/tablecloths/tablecloths-conference-studio.jpg',
    buttons: [
      { label: 'Carpet Mats', to: '/services/carpet-mats' },
    ],
  },
  {
    id: 'on-the-table',
    title: 'On the table',
    lead: 'Branded tablecloths and eye-level tabletop POS displays.',
    body: 'Turn any folding table into a professional branded stand with custom fitted tablecloths or runners, paired with table talkers, strut cards and acrylic counter holders.',
    image: '/home/services/tablecloths.jpg',
    buttons: [
      { label: 'Tablecloths', to: '/services/tablecloths' },
      { label: 'Tabletop POS', to: '/services/tabletop-pos' },
    ],
  },
];

const steps = [
  {
    title: 'Get a free quote',
    text: 'Call, email or message us with what you need. We will talk through the options that suit your space and budget.',
  },
  {
    title: 'Design and mockup',
    text: 'Send us your logo and our design team will put together a visual of how it will look.',
  },
  {
    title: 'Approve your order',
    text: 'Sign off the mockup and we go into production in our Kidderminster unit. Most jobs complete within 5 to 10 working days.',
  },
  {
    title: 'Collect or deliver',
    text: 'We will let you know when it is ready to collect from our showroom, or arrange delivery.',
  },
];

const displayFaqs = [
  {
    q: 'Can you help with artwork and mockups?',
    a: 'Yes. Send us your logo and our design team will put together a visual of how it will look, with mockups and visuals at no extra cost.',
  },
  {
    q: 'How does collection or delivery work?',
    a: 'We will let you know when it is ready to collect from our showroom, or arrange delivery.',
  },
];

const AllDisplay: React.FC = () => {
  const { openContactModal } = useContactModal();

  const scrollToTypes = () => {
    document.querySelector('#what-we-make')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-white text-black">
      {/* Hero: grid motion background */}
      <section
        className="relative overflow-hidden bg-black text-white min-h-[70svh] lg:h-[calc(60svh_-_var(--header-h,0px))] lg:min-h-[540px] flex items-center"
      >
        <div className="absolute inset-0 isolate">
          <SlimGridMotion items={gridImages} gradientColor="transparent" />
        </div>
        <div className="absolute inset-0 z-[1] bg-gradient-to-r from-black/95 via-black/70 to-black/30" />
        <div className="absolute inset-0 z-[1] bg-gradient-to-t from-black/70 via-transparent to-black/40" />

        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 py-16">
          <div className="max-w-3xl">
            <div className="flex items-center gap-5 mb-5">
              <span className="h-px w-12 bg-[#78BE20]" />
              <span className="font-poppins text-[11px] md:text-xs font-semibold tracking-[0.25em] uppercase">
                Display
              </span>
            </div>
            <h1 className="font-montserrat font-extrabold leading-[1.02] tracking-[-0.03em] text-[clamp(34px,4vw,60px)]">
              Brand your space and{' '}
              <span className="text-[#78BE20]">bring every event to life.</span>
            </h1>
            <p className="font-poppins text-base md:text-lg leading-7 text-white/80 mt-6 max-w-xl">
              Flags, gazebos, parasols and barriers outdoors. Pop-up stands, tablecloths, mats and
              tabletop displays indoors. Designed and printed from our Kidderminster unit.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <GreenButton onClick={scrollToTypes}>Explore Options</GreenButton>
              <GreenButton onClick={openContactModal} variant="outline" className="text-white">
                Get in Touch
              </GreenButton>
            </div>
          </div>
        </div>
      </section>

      {/* "What do we make?" dropdowns */}
      <section id="what-we-make" className="py-16 md:py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <Reveal className="mb-12 lg:mb-16">
            <SectionHeading title="What do we make?" tone="green" underline />
          </Reveal>
          <OptionsAccordion items={displayOptions} />
        </div>
      </section>

      {/* Green USP Bar */}
      <UspStrip
        items={[
          'MADE TO ORDER',
          'IN-HOUSE DESIGN',
          'FAST TURNAROUND',
        ]}
      />

      {/* Process on black */}
      <HowItWorks steps={steps} tone="dark" />

      {/* Questions, answered on black */}
      <section className="py-16 md:py-20 px-6 bg-black text-white border-t border-white/10">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-10 lg:gap-20 items-start">
          <Reveal>
            <h2 className="font-montserrat font-extrabold tracking-[-0.03em] leading-[1.05] text-[clamp(30px,3.4vw,44px)]">
              Questions, answered
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <div className="divide-y divide-white/10 border-y border-white/10">
              {displayFaqs.map((f) => (
                <details key={f.q} className="group">
                  <summary className="flex items-center justify-between gap-6 py-5 cursor-pointer list-none font-montserrat font-bold text-base md:text-lg [&::-webkit-details-marker]:hidden">
                    {f.q}
                    <span className="shrink-0 w-5 h-5 text-white/40 transition-transform duration-500 group-open:rotate-45 group-open:text-[#78BE20]">
                      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M10 4v12M4 10h12" />
                      </svg>
                    </span>
                  </summary>
                  <div className="pb-6">
                    <p className="font-poppins text-sm md:text-base leading-7 text-white/70 max-w-xl">{f.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Green CTA band */}
      <section className="w-full py-20 md:py-28 px-6 bg-[#78BE20] text-white">
        <div className="max-w-[900px] mx-auto text-center">
          <Reveal>
            <h2 className="font-montserrat font-extrabold tracking-[-0.03em] leading-[1.05] text-[clamp(36px,5vw,64px)] mb-4">
              Ready to get started?
            </h2>
            <p className="font-poppins text-white/85 text-base md:text-lg mb-10 max-w-xl mx-auto">
              Free quote and design mockup for any of our display products.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                type="button"
                onClick={openContactModal}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-black font-poppins font-semibold uppercase tracking-[0.12em] text-[11px] hover:bg-black hover:text-white transition-colors"
              >
                Get in touch
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </button>
              <a
                href="tel:01562227117"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-white text-white font-poppins font-semibold uppercase tracking-[0.12em] text-[11px] hover:bg-white hover:text-black transition-colors"
              >
                Call 01562 227 117
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default AllDisplay;
